// Type-only on purpose: the slice reads `window.config` at module load, which
// a unit test running in node does not have.
import type { OwnerProfilePrefs } from '@/store/slices/userPreferences';
import type { BlogArticle, BlogAuthor } from '../../mock-data/types';

/**
 * Which byline a new article and an Autopilot article start from, when the
 * owner has not typed one themselves.
 *
 * The remembered author (the byline the owner last saved an article under,
 * `selectBlogAuthorPref`) comes first. Without one, a brand where nobody has
 * saved an article yet used to start with no byline at all, and Autopilot then
 * sent a nameless author; the owner's own profile name is now the last resort
 * before that.
 */

/** The two profile fields a byline is made from. */
type ProfileName = Pick<OwnerProfilePrefs, 'firstName' | 'lastName'>;

/**
 * The owner's profile name as a byline: "firstName lastName", each half
 * trimmed, and every other field empty. Null when both halves are blank or no
 * profile was ever saved.
 *
 * Each field is type-checked rather than trusted: the preferences bag is
 * untyped (`selectOwnerProfilePrefs` casts it), so a malformed record reads as
 * blank instead of reaching the byline as something that is not a string.
 */
export const profileByline = (profile: ProfileName | null | undefined): BlogAuthor | null => {
  const half = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
  const name = [half(profile?.firstName), half(profile?.lastName)].filter((part) => part !== '').join(' ');
  if (!name) return null;
  return { name, role: '', bio: '', avatarUrl: '', url: '', links: [] };
};

/**
 * What turning Autopilot on sends as its byline, and which sentence of the
 * confirmation says so:
 *
 * - `signed`: articles are signed by `author` (the remembered author, or the
 *   owner's profile name);
 * - `latest-article`: the PUT carries the nameless author (`null` here, which
 *   the client puts on the wire as `{ name: '' }`), so a byline stored by an
 *   earlier enable is cleared and the service signs with the most recently
 *   updated article that has one (`docs/auto-generation.md` in spark-blog);
 * - `none`: the same nameless author, with no signed article for the service
 *   to fall back to, so the articles go out without a byline.
 */
export type AutopilotByline =
  | { copy: 'signed'; author: BlogAuthor }
  | { copy: 'latest-article'; author: null }
  | { copy: 'none'; author: null };

export type AutopilotBylineSources = {
  /** The remembered author, or null when none is remembered. */
  remembered: BlogAuthor | null;
  /** The blog's articles, as loaded; only their bylines are read. */
  articles: ReadonlyArray<Pick<BlogArticle, 'author'>>;
  /** The owner's saved profile, or null when none was saved. */
  profile: ProfileName | null;
};

const named = (author: BlogAuthor | null | undefined): author is BlogAuthor => Boolean(author?.name.trim());

/**
 * In order: the remembered author when it has a name; else, when any loaded
 * article is signed, the nameless author, so the service reuses that byline
 * (the profile never jumps ahead of a name the owner has actually published
 * under); else the profile byline; else the nameless author, and no byline.
 */
export const autopilotByline = ({ remembered, articles, profile }: AutopilotBylineSources): AutopilotByline => {
  if (named(remembered)) return { copy: 'signed', author: remembered };
  if (articles.some((article) => named(article.author))) return { copy: 'latest-article', author: null };
  const fromProfile = profileByline(profile);
  if (fromProfile) return { copy: 'signed', author: fromProfile };
  return { copy: 'none', author: null };
};
